import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/apiClient';
import { ShieldAlert, Heart, Copy, Check, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  anonymousReportFormSchema,
  categoryLabels,
  severityLabels,
  contactTypeLabels,
  type AnonymousReportFormData,
} from '@/schemas/anonymousReportSchemas';

interface AnonymousReportFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

// Genera código de seguimiento único
function generateTrackingCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Sin I, O, 0, 1 para evitar confusión
  let code = 'AR-';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

export function AnonymousReportForm({ open, onOpenChange, onSuccess }: AnonymousReportFormProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [successCode, setSuccessCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<AnonymousReportFormData>({
    resolver: zodResolver(anonymousReportFormSchema),
    defaultValues: {
      category: 'other',
      severity: 'medium',
      request_professional_contact: false,
    },
  });

  const category = watch('category');
  const severity = watch('severity');
  const requestContact = watch('request_professional_contact');

  const handleCopyCode = async () => {
    if (successCode) {
      await navigator.clipboard.writeText(successCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({
        title: 'Copiado',
        description: 'Código copiado al portapapeles',
      });
    }
  };

  const onSubmit = async (data: AnonymousReportFormData) => {
    setLoading(true);
    try {
      const trackingCode = generateTrackingCode();

      const payload = {
        tracking_code: trackingCode,
        category: data.category,
        subject: data.subject,
        description: data.description,
        severity: data.severity,
        request_professional_contact: data.request_professional_contact,
        contact_type: data.request_professional_contact ? data.contact_type : null,
        status: 'pending',
      };

      const { error } = await api
        .from('anonymous_reports')
        .insert(payload);

      if (error) throw error;

      setSuccessCode(trackingCode);
      reset();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo enviar el reporte',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccessCode(null);
    setCopied(false);
    onOpenChange(false);
  };

  // Vista de éxito con código
  if (successCode) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="w-6 h-6" />
              Reporte Enviado
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <Alert className="border-green-500/50 bg-green-500/10">
              <AlertDescription className="text-center">
                Tu reporte ha sido enviado de forma <strong>completamente anónima</strong>.
                Nadie puede saber quién lo envió.
              </AlertDescription>
            </Alert>

            <Card className="p-6 text-center bg-gradient-to-br from-primary/5 to-primary/10">
              <p className="text-sm text-muted-foreground mb-2">Tu código de seguimiento:</p>
              <div className="flex items-center justify-center gap-2">
                <code className="text-3xl font-mono font-bold tracking-wider text-primary">
                  {successCode}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCopyCode}
                  className="h-8 w-8"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </Card>

            <Alert variant="default" className="border-amber-500/50 bg-amber-500/10">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription className="text-amber-700 dark:text-amber-400">
                <strong>Importante:</strong> Guarda este código para consultar el estado de tu reporte.
                No podrás recuperarlo después.
              </AlertDescription>
            </Alert>

            <Button onClick={handleClose} className="w-full">
              Entendido, cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-primary" />
            Reporte Anónimo
          </DialogTitle>
          <DialogDescription>
            Este formulario es completamente anónimo. Tu identidad no será registrada.
          </DialogDescription>
        </DialogHeader>

        <Alert className="border-blue-500/50 bg-blue-500/10">
          <ShieldAlert className="h-4 w-4 text-blue-500" />
          <AlertDescription className="text-blue-700 dark:text-blue-400">
            Tu reporte será tratado con total confidencialidad. No almacenamos
            información que pueda identificarte.
          </AlertDescription>
        </Alert>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Categoría */}
          <div className="space-y-2">
            <Label>Categoría del reporte *</Label>
            <Select
              value={category}
              onValueChange={(value) => setValue('category', value as any)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selecciona una categoría" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(categoryLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.category && (
              <p className="text-sm text-destructive">{errors.category.message}</p>
            )}
          </div>

          {/* Asunto */}
          <div className="space-y-2">
            <Label htmlFor="subject">Asunto *</Label>
            <Input
              id="subject"
              placeholder="Breve descripción del tema"
              {...register('subject')}
            />
            {errors.subject && (
              <p className="text-sm text-destructive">{errors.subject.message}</p>
            )}
          </div>

          {/* Descripción */}
          <div className="space-y-2">
            <Label htmlFor="description">Descripción detallada *</Label>
            <Textarea
              id="description"
              placeholder="Describe la situación con el mayor detalle posible..."
              rows={5}
              {...register('description')}
            />
            {errors.description && (
              <p className="text-sm text-destructive">{errors.description.message}</p>
            )}
          </div>

          {/* Severidad */}
          <div className="space-y-2">
            <Label>Nivel de urgencia</Label>
            <Select
              value={severity}
              onValueChange={(value) => setValue('severity', value as any)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(severityLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Solicitar contacto profesional */}
          <Card className="p-4 border-pink-500/30 bg-pink-500/5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Heart className="w-5 h-5 text-pink-500 mt-0.5" />
                <div>
                  <Label htmlFor="request_contact" className="cursor-pointer font-medium">
                    Solicitar contacto con profesional de salud
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    La empresa coordinará una cita con un profesional.
                    Recibirás instrucciones al consultar tu código.
                  </p>
                </div>
              </div>
              <Switch
                id="request_contact"
                checked={requestContact}
                onCheckedChange={(checked) => setValue('request_professional_contact', checked)}
              />
            </div>

            {requestContact && (
              <div className="mt-4 pt-4 border-t border-pink-500/20">
                <Label>Tipo de profesional</Label>
                <Select
                  value={watch('contact_type') || ''}
                  onValueChange={(value) => setValue('contact_type', value as any)}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Selecciona tipo de profesional" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(contactTypeLabels).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </Card>

          {/* Botones */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-gradient-magma"
            >
              {loading ? 'Enviando...' : 'Enviar Reporte Anónimo'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
